import { Alert, Box, CircularProgress, Grid, MenuItem, Select, Skeleton, TextField, Typography } from '@mui/material';
import {
  cloneDeep,
  compact,
  concat,
  difference,
  filter,
  find,
  flatMap,
  forEach,
  get,
  groupBy,
  includes,
  indexOf,
  isEmpty,
  isEqual,
  isNumber,
  keyBy,
  keys,
  map,
  pickBy,
  replace,
  set,
  size,
  some,
  uniq,
  uniqWith,
  values,
} from 'lodash';
import { useSnackbar } from 'notistack';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';

import { DialogWithCloseButton } from 'components/atoms/Dialog/DialogWithCloseButton';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import {
  modelTypeArea,
  modelTypeCell,
  modelTypeDefect,
  modelTypeTls,
} from 'components/Pages/Jobs/inferenceFieldsOptions';
import { ActionStepper } from 'components/StudyDashboard/ProceduresPage/Actions/ActionStepper';
import { LoadFromPreviousJobStep } from 'components/StudyDashboard/ProceduresPage/Actions/LoadFromPreviousJobStep';
import { usePreviousJobsAvailability } from 'components/StudyDashboard/ProceduresPage/Actions/LoadFromPreviousJobStep/usePreviousJobsAvailability';
import { useRunCalculateFeaturesMutation } from 'components/StudyDashboard/ProceduresPage/Actions/PostProcessingAndCalculateFeatures/useRunCalculateFeaturesMutation';
import { AutomaticCondition } from 'interfaces/automaticCondition';
import {
  getModelsTypeByModelInferences,
  getResultNameTypesByOrchestrations,
  Input,
  Inputs,
  ModelInference,
} from 'interfaces/calculateFeatures';
import GridBasedCalculationParams from 'interfaces/girdBasedCalculationParams';
import { AttributesTable } from 'interfaces/inferenceArtifacts';
import {
  CalculateFeaturesParams,
  CellTypingJob,
  hashDepUrlParameterRegex,
  JobType,
  NeighborhoodClustersJob,
  ThresholdJob,
} from 'interfaces/job';
import { JobPreset } from 'interfaces/jobPreset';
import { CellRules } from 'interfaces/jobs/multiplex/cellTypingParams';
import { PipRunName, PipRunNameEnum } from 'interfaces/jobs/pipsRunNames';
import {
  ActionInput,
  InputSource,
  PostProcessingActionCreated,
  Input as PostProcessingInput,
} from 'interfaces/postProcessingAction';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { VisualizationCreated } from 'interfaces/visualization';
import {
  AssignmentByStain,
  getSelectedAssignmentDetails,
  SelectedAssignmentDetails,
  useAssignmentManagement,
} from 'utils/assignmentManagement';
import { humanize, uuidv4 } from 'utils/helpers';
import { ModelPreferencesById, useModelPreferences } from 'utils/models';
import { useOrchestrationSelections } from 'utils/orchestrationSelections';
import { useSlidesDataForArtifactsResultsQuery } from 'utils/queryHooks/calculateFeatures';
import useTaxonomy from 'utils/queryHooks/taxonomy/useTaxonomy';
import { useClassificationModelOptions, useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import { useAnnotationAssignmentByStain } from 'utils/queryHooks/useAnnotationAssignmentQueries';
import { useArtifactQueries } from 'utils/queryHooks/useArtifactQueries';
import { useModelTypeOptions } from 'utils/queryHooks/useModelTypeOptions';
import { usePipsRunNames } from 'utils/queryHooks/usePipsRunNames';
import { getSlidesRegistrationWithStains, RegistrationDetails, useSlideRegistrations } from 'utils/slideRegistrations';
import { CasesParams } from 'utils/useCasesParams/types';
import useStudy from 'utils/useStudy';
import { getEnabledAreaTransferTargetsBySourceStain, getRegisteredAreaOptionsByTargetStain } from './areaTransfers';
import {
  BinaryClassifierSelectionMode,
  hasBinaryClassifierMultipleSelections,
  normalizeBinaryClassifierSelectionsForMode,
} from './binaryClassifierSelections';
import {
  buildArtifactCategoryByUrl,
  getMissingInputsBySlideId,
  getPipsStudyInputsState,
  shouldHidePostProcessingInputsByDefault,
} from './calculateFeaturesInputs';
import { buildCasesManifest, buildInputsPerCasePerSlide } from './calculateFeaturesPayload';
import {
  deriveClassConfigFromSelections,
  type ClassConfigs,
  type OrchestrationMetadata,
} from './classConfigFromSelections';
import { getInitialConfigurationSource, isConfigurationSource } from './configurationSource';
import { FeatureActions } from './FeaturesStep';
import { CellPopulationGroupState } from './FeaturesStep/cellPopulationGroupRestore';
import GridBasedCalculationParamsStep from './GridBasedCalculationParamsStep';
import InferenceModelsForSlides, { ThresholdJobSelectionMode } from './InferenceModelsForSlides';
import { PostProcessingActions } from './PostProcessingActions';
import {
  getInstanceCountThresholdOptions,
  INSTANCE_COUNT_POSITIVITY_ACTION_NAME,
  INSTANCE_COUNT_THRESHOLDS_INPUT_KEY,
} from './PostProcessingActions/instanceCountPositivity';
import {
  getOutOfSyncSpotDetectionActions,
  getStainsWithSpotDetectionAndCellSegmentation,
  getSuggestedSpotDetectionActionsByStain,
  INSTANCE_H_SCORE_ACTION_NAME,
  INSTANCE_H_SCORE_BINNED_COLUMNS_INPUT_KEY,
  SpotDetectionActionSuggestion,
  SpotDetectionActionUpdate,
} from './PostProcessingActions/spotDetectionActionSuggestions';
import { SpotDetectionActionSuggestionsBanner } from './PostProcessingActions/SpotDetectionActionSuggestionsBanner';
import usePostProcessingActions, {
  allCellsOption,
  getKmeansCellLabelColumns,
  useActionsQuery,
} from './PostProcessingActions/usePostProcessingActions';
import { createNewAction } from './PostProcessingActions/utils';
import { PresetsStep } from './Presets/PresetsStep';
import { extractNewCellsFromPreset, HighlightedPresetInput } from './Presets/presetUtils';
import { SaveUpdatePresetButtons } from './Presets/SaveUpdatePresetButtons';
import {
  buildPreviousJobSelectedResultsSnapshotFromInputs,
  buildSelectedSlideRegistrationsByOrchestration,
} from './previousJobSelectionSnapshots';
import { ConfigurationSource, StartFromStep } from './StartFromStep';
import { buildThresholdMergeState, ThresholdConflictResolutions } from './thresholdMerge';
import { usePreviousJobConfigurationStatus } from './usePreviousJobConfigurationStatus';
import { usePreviousJobRestore } from './usePreviousJobRestore';
import { usePreviousJobSelectionStatus } from './usePreviousJobSelectionStatus';
import {
  convertFeatureActionsFromBackend,
  convertFeatureActionsToBackend,
  convertPostProcessingActionsFromBackend,
  convertPostProcessingActionsToBackend,
  doesSpecificModelExist,
  getAdditionalInputError,
} from './utils';
import {
  arePostProcessingActionsValid,
  isGridParamsStepValid,
  isInferenceResultsStepValid,
  isVisualizationStepValid,
} from './validation';
import { VisualizationStep } from './VisualizationStep';

const SNACK_BAR_KEY_CALCULATE_FEATURES = 'CALCULATE_FEATURES';

export interface NewCell {
  name: string;
  value: AutomaticCondition | null;
  stain: string;
  populationGroup?: CellPopulationGroupState;
}

enum PostProcessingAndCalculateFeaturesSteps {
  StartFrom = 'Start from',
  SelectPipsName = 'Pipeline Stage Name',
  SelectModels = 'Inference Model And Assignments For Slides',
  Postprocessing = 'Post Processing Actions',
  Features = 'Features',
  GridBasedCalculation = 'Grid based params',
  Visualizations = 'Visualizations',
  SummaryAndSave = 'Summary and Save',
}

const defaultPipsRunName = 'generic_pips_run';

interface PostProcessingAndCalculateFeaturesProps {
  onClose: (event?: any, force?: boolean) => void;
  jobId?: string;
  casesParams: CasesParams;
  additionalParams?: { pipRunName?: string } | null;
  platformVersionWarning?: React.ReactNode;
}

export const PostProcessingAndCalculateFeatures: React.FC<
  React.PropsWithChildren<PostProcessingAndCalculateFeaturesProps>
> = ({ additionalParams, onClose, casesParams, jobId, platformVersionWarning }) => {
  const orchestrationSelectionsState = useOrchestrationSelections();
  const {
    selectedOrchestrations,
    selectedPostprocessedOrchestrations,
    selectedPipsOrchestrations,
    selectedPipsAreaInstancesOrchestrations,
    selectedCellQuantificationOrchestrations,
    selectedAreaQuantificationOrchestrations,
    selectedSlideSegmentationsTableOrchestrations,
    selectedSlideDetectionsTableOrchestrations,
    allSelectedNonModelOrchestrations,
    setSelectedOrchestrations,
    setSelectedPostprocessedOrchestrations,
    setSelectedPipsOrchestrations,
    setSelectedPipsAreaInstancesOrchestrations,
    setSelectedCellQuantificationOrchestrations,
    setSelectedAreaQuantificationOrchestrations,
    setSelectedSlideSegmentationsTableOrchestrations,
    setSelectedSlideDetectionsTableOrchestrations,
  } = orchestrationSelectionsState;
  const [hidePostProcessingInputs, setHidePostProcessingInputs] = useState(() =>
    shouldHidePostProcessingInputsByDefault(additionalParams?.pipRunName || defaultPipsRunName)
  );
  const selectedNonModelOrchestrationsForRun = useMemo(() => {
    if (!hidePostProcessingInputs) {
      return allSelectedNonModelOrchestrations;
    }

    return [
      selectedCellQuantificationOrchestrations,
      selectedAreaQuantificationOrchestrations,
      selectedSlideSegmentationsTableOrchestrations,
      selectedSlideDetectionsTableOrchestrations,
    ];
  }, [
    hidePostProcessingInputs,
    allSelectedNonModelOrchestrations,
    selectedCellQuantificationOrchestrations,
    selectedAreaQuantificationOrchestrations,
    selectedSlideSegmentationsTableOrchestrations,
    selectedSlideDetectionsTableOrchestrations,
  ]);

  // Job configuration state
  const [postProcessingActions, setPostProcessingActions] = useState<PostProcessingActionCreated[]>([]);
  const [featureActions, setFeatureActions] = useState<PostProcessingActionCreated[]>([]);
  const [gridBasedCalculationParams, setGridBasedCalculationParams] = useState<
    Record<string, GridBasedCalculationParams>
  >({});
  const [newCellsCreated, setNewCellsCreated] = useState<Record<string, NewCell>>({});
  const [visualizations, setVisualizations] = useState<VisualizationCreated[]>([]);
  const [postProcessingClasses, setPostProcessingClasses] = useState<ClassConfigs>({});
  const [highlightedInputs, setHighlightedInputs] = useState<HighlightedPresetInput[]>([]);

  // Assignment management state
  const assignmentManagementState = useAssignmentManagement();
  const { selectedAssignments, setSelectedAssignments } = assignmentManagementState;

  // Class configuration state
  const [classConfigWithSpecialClasses, setClassConfigWithSpecialClasses] = useState<ClassConfigs>({});
  const [orchestrationMetadata, setOrchestrationMetadata] = useState<OrchestrationMetadata>({});

  const currentStains = useMemo(
    () => uniq(concat(keys(classConfigWithSpecialClasses), keys(orchestrationMetadata))),
    [classConfigWithSpecialClasses, orchestrationMetadata]
  );

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const confirmWithModal = useConfirmation();
  const { stainTypeOptions } = useStainTypeOptions();
  const stainTypeIdToDisplayName = (stain: string) => find(stainTypeOptions, { id: stain })?.displayName || stain;
  const { data: taxonomies = [] } = useTaxonomy();
  const { study } = useStudy(casesParams.studyId);
  const qcMarkersSettings = study?.settings?.qcMarkersSettings || {};

  // Step management state
  const [activeStep, setActiveStepState] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);
  const setActiveStep = useCallback(
    (params: Parameters<typeof setActiveStepState>[0]) => {
      setActiveStepState((prevActiveStep) => {
        const nextActiveStep =
          typeof params === 'function' ? (params as (prev: number) => number)(prevActiveStep) : params;
        setMaxStepReached((previousMax) => Math.max(previousMax, nextActiveStep));
        return nextActiveStep;
      });
    },
    [setActiveStepState, setMaxStepReached]
  );
  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});

  // Job rebuild state
  const [hasShownMissingInputsSnackbar, setHasShownMissingInputsSnackbar] = useState(false);

  // Model preferences state
  const {
    modelPreferencesByModelId,
    setModelPreferencesState,
    setModelPreferencesByModelId,
    setIntensityClassificationBinning,
  } = useModelPreferences();

  const {
    data: slides,
    isFetched: isSlidesFetched,
    isError: isSlidesError,
  } = useSlidesDataForArtifactsResultsQuery(casesParams);
  const slideStainTypes = uniq(map(slides, 'stainingType'));
  const slideRegistrationsContext = useSlideRegistrations(casesParams, slideStainTypes, casesParams.studyId);
  const { selectedSlideRegistrations, setSelectedSlideRegistration } = slideRegistrationsContext;

  // Multiplex state
  const [thresholdJobSelectionMode, setThresholdJobSelectionModeState] = useState<ThresholdJobSelectionMode>('single');
  const [binaryClassifierSelectionMode, setBinaryClassifierSelectionModeState] =
    useState<BinaryClassifierSelectionMode>('single');
  const [selectedThresholdJobId, setSelectedThresholdJobId] = useState<string | null>(null);
  const [selectedThresholdJobIds, setSelectedThresholdJobIds] = useState<string[]>([]);
  const [selectedThresholdJobs, setSelectedThresholdJobs] = useState<ThresholdJob[]>([]);
  const [thresholdConflictResolutions, setThresholdConflictResolutions] = useState<ThresholdConflictResolutions>({});
  const [restoredThresholdJobOrchestrationId, setRestoredThresholdJobOrchestrationId] = useState<string | null>(null);
  const [selectedCellTypingJobId, setSelectedCellTypingJobId] = useState<string | null>(null);
  const [restoredCellTypingJobOrchestrationId, setRestoredCellTypingJobOrchestrationId] = useState<string | null>(null);
  const [selectedNeighborhoodClustersJobId, setSelectedNeighborhoodClustersJobId] = useState<string | null>(null);
  const [restoredNeighborhoodClustersJobOrchestrationId, setRestoredNeighborhoodClustersJobOrchestrationId] = useState<
    string | null
  >(null);
  const [thresholds, setThresholds] = useState<Record<string, number> | null>(null);
  const [perMarkerThresholdsOrchestrationId, setPerMarkerThresholdsOrchestrationId] = useState<string | null>(null);
  const [cellRules, setCellRules] = useState<any>(null);
  const [restoredCellRules, setRestoredCellRules] = useState<CellRules | null>(null);
  const [selectedCellRulesPanelId, setSelectedCellRulesPanelId] = useState<string | null | undefined>(undefined);
  const [knnArtifactUrl, setKnnArtifactUrl] = useState<string>(null);
  const [knnFileType, setKnnFileType] = useState<string>(null);
  const [kMeansClusteringResults, setKMeansClusteringResults] = useState<any[]>(null);

  // Job summary state
  const [jobSummaryData, setJobSummaryData] = useState<{
    name: string;
    description: string;
  }>({
    name: '',
    description: '',
  });

  const onThresholdJobSelect = (newThresholdJob: ThresholdJob) => {
    const newThresholds = newThresholdJob?.results?.perMarkerThresholds;
    setThresholds(newThresholds);
    setPerMarkerThresholdsOrchestrationId(newThresholdJob?.orchestrationId);
  };

  const thresholdMergeState = useMemo(
    () => buildThresholdMergeState(selectedThresholdJobs, thresholdConflictResolutions),
    [selectedThresholdJobs, thresholdConflictResolutions]
  );

  const onThresholdJobsSelect = useCallback((newThresholdJobs: ThresholdJob[]) => {
    setSelectedThresholdJobs(newThresholdJobs);
  }, []);

  const setThresholdJobSelectionMode = useCallback(
    (nextMode: ThresholdJobSelectionMode) => {
      setThresholdJobSelectionModeState(nextMode);

      if (nextMode === 'multiple') {
        setSelectedThresholdJobIds((currentJobIds) =>
          !isEmpty(currentJobIds) || !selectedThresholdJobId ? currentJobIds : [selectedThresholdJobId]
        );
        return;
      }

      setSelectedThresholdJobIds([]);
      setSelectedThresholdJobs([]);
      setThresholdConflictResolutions({});

      if (!selectedThresholdJobId) {
        setThresholds(null);
        setPerMarkerThresholdsOrchestrationId(null);
      }
    },
    [selectedThresholdJobId]
  );

  const setBinaryClassifierSelectionMode = useCallback(
    (nextMode: BinaryClassifierSelectionMode) => {
      setBinaryClassifierSelectionModeState(nextMode);
      setSelectedOrchestrations((currentSelectedOrchestrations) =>
        normalizeBinaryClassifierSelectionsForMode(currentSelectedOrchestrations, nextMode)
      );
    },
    [setSelectedOrchestrations]
  );

  useEffect(() => {
    if (thresholdJobSelectionMode !== 'multiple') {
      return;
    }

    setThresholds(isEmpty(thresholdMergeState.thresholds) ? null : thresholdMergeState.thresholds);
    setPerMarkerThresholdsOrchestrationId(thresholdMergeState.perMarkerThresholdsOrchestrationId);
  }, [thresholdJobSelectionMode, thresholdMergeState]);

  const cellTypingJobError =
    selectedCellTypingJobId && (!knnArtifactUrl || !knnFileType)
      ? 'The selected cell typing job does not have the required KNN artifact'
      : null;

  const onCellRulesChange = (newCellRules: CellRules | null) => {
    setCellRules(newCellRules);
  };

  const onCellTypingJobSelect = (cellTypingJob: CellTypingJob) => {
    const newKnn = cellTypingJob?.results?.slideInferenceMultiplexMarkersProbabilities;
    // Fit KNN artifact URLs are saved with hash_deps query parameter, we want to change it to meta.orchestration_id to support parent->child artifact linking
    // e.g. from "artifact://studies/c536d31e-127b-43e9-9b4e-c3b025ca4cfa/knn_classifier.knn_classifier.pickle?hash_deps=fe64014f3b410c761b5c0a245c209dbf"
    //      to "artifact://studies/c536d31e-127b-43e9-9b4e-c3b025ca4cfa/knn_classifier.knn_classifier.pickle?meta.orchestration_id=#############"
    const artifactUrlWithOrchId = !hashDepUrlParameterRegex.test(newKnn?.artifactUrl || '')
      ? newKnn?.artifactUrl
      : replace(
          newKnn?.artifactUrl,
          hashDepUrlParameterRegex,
          `meta.orchestration_id=${cellTypingJob.orchestrationId}`
        );
    setKnnArtifactUrl(artifactUrlWithOrchId);
    setKnnFileType(newKnn?.fileType);
  };

  const onNeighborhoodClustersJobSelect = (neighborhoodClustersJob: NeighborhoodClustersJob) => {
    const newKMeansClusteringResults = neighborhoodClustersJob?.results?.kMeansClusteringResults;
    setRestoredNeighborhoodClustersJobOrchestrationId(neighborhoodClustersJob?.orchestrationId || null);
    setKMeansClusteringResults(newKMeansClusteringResults);
  };

  const handlePreviousJobReset = useCallback(() => {
    setRestoredCellRules(null);
    setSelectedCellRulesPanelId(undefined);
  }, []);

  const { data: modelTypes, isError: modelTypesError } = useModelTypeOptions({ retry: false });
  const modelTypesByType = groupBy(modelTypes, 'type');

  const gridBasedFeaturesStains = useMemo(
    () =>
      uniq(
        flatMap(
          filter(featureActions, (action) => !isEmpty(action?.action_params?.grid_based_params?.reduction_type)),
          'stains'
        )
      ),
    [featureActions]
  );

  useEffect(() => {
    // Omit stains that not longer have grid based features
    setGridBasedCalculationParams((previousParams) => {
      return pickBy(previousParams, (_, stain) => includes(gridBasedFeaturesStains, stain));
    });
  }, [gridBasedFeaturesStains]);

  const { classificationModelOptions } = useClassificationModelOptions();

  const selectedClassificationModels = map(values(modelPreferencesByModelId), (item) => item.readAs);
  const selectedClassificationModelsOptions = filter(classificationModelOptions, (option) =>
    some(selectedClassificationModels, (selectedClassificationModel) => selectedClassificationModel === option.id)
  );
  // get me the modelsId or readAs that have numBins or mapValuesToBins
  const binsModels = useMemo(() => {
    const binsModelIds: string[] = [];
    forEach(modelPreferencesByModelId, (modelPreferences, modelId) => {
      if (modelPreferences?.numBins || !isEmpty(modelPreferences?.mapValuesToBins)) {
        if (
          modelPreferences?.readAs &&
          find(classificationModelOptions, { id: modelPreferences.readAs })?.needBinningParams
        ) {
          binsModelIds.push(modelPreferences.readAs);
        } else {
          binsModelIds.push(modelId);
        }
      }
    });
    return binsModelIds;
  }, [modelPreferencesByModelId]);

  const {
    getOptions,
    getOptionsByValue,
    getOutputNamesForAllStains,
    getMappingFiltersMetadataForLogicalQuery,
    spotDetectionCountBinCells,
  } = usePostProcessingActions(
    currentStains,
    classConfigWithSpecialClasses,
    orchestrationMetadata,
    newCellsCreated,
    thresholds,
    kMeansClusteringResults,
    binsModels,
    getRegisteredAreaOptionsByTargetStain(
      getEnabledAreaTransferTargetsBySourceStain(
        slides,
        selectedOrchestrations,
        selectedSlideRegistrations,
        modelPreferencesByModelId
      ),
      classConfigWithSpecialClasses
    ),
    modelPreferencesByModelId,
    casesParams.studyId
  );

  const { postProcessingActionsById } = useActionsQuery();

  const setCalculateFeatureParams = (params: {
    postprocessing: PostProcessingActionCreated[];
    features: PostProcessingActionCreated[];
    visualizations: VisualizationCreated[];
    gridBasedCalculationParams: { [key: string]: GridBasedCalculationParams };
    modelPreferencesByModelId?: ModelPreferencesById;
    registrations: RegistrationDetails[];
    newCellsCreated?: Record<string, NewCell>;
    highlightedInputs?: HighlightedPresetInput[];
  }) => {
    setHighlightedInputs(params.highlightedInputs || []);
    // Process postprocessing actions first to get the correct context for features
    let newPostProcessingActions = postProcessingActions;
    if (!isEmpty(params.postprocessing)) {
      newPostProcessingActions = convertPostProcessingActionsFromBackend(
        postProcessingActionsById,
        params.postprocessing,
        getMappingFiltersMetadataForLogicalQuery,
        (inputSource, selectedStains, index, actionInput, inputSourceDependentOn) =>
          getOptionsByValue(
            inputSource,
            selectedStains,
            index,
            params.postprocessing,
            actionInput,
            postProcessingActionsById,
            inputSourceDependentOn
          )
      );
      setPostProcessingActions(newPostProcessingActions);
      setPostProcessingClasses(getOutputNamesForAllStains(size(params.postprocessing), params.postprocessing));
      setClassConfigFromSelectedResults(newPostProcessingActions);
    }

    const getMappingFiltersMetadataForStain = (stain: string) =>
      getMappingFiltersMetadataForLogicalQuery(
        stain,
        size(newPostProcessingActions), // Use the newly processed actions, not the state
        newPostProcessingActions, // Use the newly processed actions, not the state
        ActionInput.FEATURE_FAMILY,
        postProcessingActionsById,
        AttributesTable.CellTable
      );

    if (!isEmpty(params.features)) {
      // Use newCellsCreated from params from preset if available, otherwise create them implicitly from feature actions
      const presetNewCells = extractNewCellsFromPreset(params, getMappingFiltersMetadataForStain);

      const { newCellsCreated: newCells, convertedFeatureActions } = convertFeatureActionsFromBackend(
        postProcessingActionsById,
        params.features,
        getMappingFiltersMetadataForStain,
        presetNewCells
      );

      setFeatureActions(convertedFeatureActions);
      setNewCellsCreated(newCells);
    } else {
      // Handle case where newCellsCreated is provided separately (from preset) without features
      const presetNewCellsOnly = extractNewCellsFromPreset(params, getMappingFiltersMetadataForStain);
      if (!isEmpty(presetNewCellsOnly)) {
        setNewCellsCreated(presetNewCellsOnly);
      }
    }

    if (!isEmpty(params.gridBasedCalculationParams)) {
      setGridBasedCalculationParams(params.gridBasedCalculationParams);
    }

    if (!isEmpty(params.visualizations)) {
      setVisualizations(
        map(params.visualizations, (visualization) => ({ ...visualization, newVisualizationId: uuidv4() }))
      );
    }

    if (!isEmpty(params.modelPreferencesByModelId)) {
      setModelPreferencesState(params.modelPreferencesByModelId);
    }
  };

  const applyPipsStudyInputs = (pipsStudyInputs?: CalculateFeaturesParams['pipsStudyInputs'] | null) => {
    const pipsStudyInputsState = getPipsStudyInputsState(pipsStudyInputs);

    setThresholdJobSelectionModeState('single');
    setSelectedThresholdJobId(null);
    setSelectedThresholdJobIds([]);
    setSelectedThresholdJobs([]);
    setThresholdConflictResolutions({});
    setRestoredThresholdJobOrchestrationId(pipsStudyInputsState.perMarkerThresholdsOrchestrationId ?? null);
    setSelectedCellTypingJobId(null);
    setRestoredCellTypingJobOrchestrationId(pipsStudyInputsState.knnOrchestrationId ?? null);
    setSelectedNeighborhoodClustersJobId(null);
    setRestoredNeighborhoodClustersJobOrchestrationId(pipsStudyInputsState.neighborhoodClustersOrchestrationId ?? null);

    setThresholds(pipsStudyInputsState.thresholds ?? null);
    setPerMarkerThresholdsOrchestrationId(pipsStudyInputsState.perMarkerThresholdsOrchestrationId ?? null);
    setCellRules(pipsStudyInputsState.cellRules ?? null);
    setRestoredCellRules(pipsStudyInputsState.cellRules ?? null);
    setSelectedCellRulesPanelId(undefined);
    setKnnArtifactUrl(pipsStudyInputsState.knnArtifactUrl ?? null);
    setKnnFileType(pipsStudyInputsState.knnFileType ?? null);
    setKMeansClusteringResults(pipsStudyInputsState.kMeansClusteringResults ?? null);
  };

  const applySlideRegistrations = (registrations?: RegistrationDetails[] | null) => {
    setSelectedSlideRegistration(buildSelectedSlideRegistrationsByOrchestration(registrations));
  };

  // Add defects area type to the area class config if defect model is selected
  // Add allCellsOption to the cell class config if cell model is selected
  const getClassConfigWithSpecialClasses = (currentClassConfig: ClassConfigs) => {
    const newClassConfig = { ...currentClassConfig };

    forEach(keys(currentClassConfig), (stain) => {
      set(
        newClassConfig,
        [stain, modelTypeArea.value],
        concat(
          (newClassConfig?.[stain]?.[modelTypeArea.value] as string[]) || [],
          (newClassConfig?.[stain]?.[modelTypeTls.value] as string[]) || []
        )
      );

      if (
        doesSpecificModelExist(
          stain,
          modelTypeDefect.apiModelValue,
          selectedOrchestrations,
          selectedAssignments,
          slidesById
        )
      ) {
        const areaClasses = (newClassConfig?.[stain]?.[modelTypeArea.value] as string[]) || [];
        const defectsClasses = (newClassConfig?.[stain]?.[modelTypeDefect.value] as string[]) || [];
        set(
          newClassConfig,
          [stain, modelTypeArea.value],
          concat(areaClasses, defectsClasses, 'defects', 'defects_raw')
        );
      }

      if (
        doesSpecificModelExist(
          stain,
          modelTypeCell.apiModelValue,
          selectedOrchestrations,
          selectedAssignments,
          slidesById
        )
      ) {
        const cellClasses = (newClassConfig?.[stain]?.[modelTypeCell.value] as string[]) || [];
        set(newClassConfig, [stain, modelTypeCell.value], concat(cellClasses, allCellsOption.id));
      }

      // remove backgroundClassesToIgnore from the class config
      forEach(modelTypes, (modelType) => {
        if (newClassConfig[stain]?.[modelType.id] && modelType.backgroundClassesToIgnore) {
          set(
            newClassConfig,
            [stain, modelType.id],
            difference(newClassConfig[stain][modelType.id] as string[], modelType.backgroundClassesToIgnore)
          );
        }
      });
    });

    return newClassConfig;
  };

  const setClassConfigFromSelectedResults = (
    newPostProcessedActions: PostProcessingActionCreated[] = postProcessingActions
  ) => {
    const slidesByIdForConfig = keyBy(slides, 'slideId');
    if (isEmpty(slidesByIdForConfig)) {
      return;
    }

    const derived = deriveClassConfigFromSelections({
      selectedOrchestrations,
      allSelectedNonModelOrchestrations: selectedNonModelOrchestrationsForRun,
      selectedAssignments,
      slidesById: slidesByIdForConfig,
      modelPreferencesByModelId,
      classificationModelOptions,
    });

    const newClassConfig = derived.classConfig;
    const newOrchestrationMetadata = derived.orchestrationMetadata;

    const postProcessingActionsThatAddsColumnsFromCellRules = [
      'get_cell_types_by_marker_positivity',
      'get_cell_types_by_knn',
    ];

    const getClusterIdByKmeansActionName = 'get_cluster_id_by_kmeans';

    const hasActionsThatCreatesCellRulesColumns = some(newPostProcessedActions, (postProcessingAction) =>
      includes(postProcessingActionsThatAddsColumnsFromCellRules, postProcessingAction.actionName)
    );
    const hasGetClusterIdByKmeansAction = some(
      newPostProcessedActions,
      (postProcessingAction) => postProcessingAction.actionName === getClusterIdByKmeansActionName
    );

    if (hasActionsThatCreatesCellRulesColumns && !isEmpty(cellRules)) {
      newClassConfig[MULTIPLEX_STAIN_ID] = {
        ...newClassConfig[MULTIPLEX_STAIN_ID],
        [modelTypeCell.value]: uniq(
          concat(newClassConfig[MULTIPLEX_STAIN_ID]?.[modelTypeCell.value] as string[], keys(cellRules))
        ),
      };
    }

    if (hasGetClusterIdByKmeansAction && !isEmpty(kMeansClusteringResults)) {
      const newCellsFromKmeans = getKmeansCellLabelColumns(kMeansClusteringResults);
      newClassConfig[MULTIPLEX_STAIN_ID] = {
        ...newClassConfig[MULTIPLEX_STAIN_ID],
        [modelTypeCell.value]: uniq(
          concat(newClassConfig[MULTIPLEX_STAIN_ID]?.[modelTypeCell.value] as string[], newCellsFromKmeans)
        ),
      };
    }

    const nextClassConfigWithSpecialClasses = getClassConfigWithSpecialClasses(newClassConfig);

    if (!isEqual(newOrchestrationMetadata, orchestrationMetadata)) {
      setOrchestrationMetadata(newOrchestrationMetadata);
    }

    if (!isEqual(nextClassConfigWithSpecialClasses, classConfigWithSpecialClasses)) {
      setClassConfigWithSpecialClasses(nextClassConfigWithSpecialClasses);
    }
  };

  const updateClassConfigFromSelectedPostProcessingActions = (
    newPostProcessingActions: PostProcessingActionCreated[]
  ) => {
    setClassConfigFromSelectedResults(newPostProcessingActions);
  };

  // Instance H-Score/Positivity need both spot detection counts and a cell segmentation to bin them
  // into, so only surface the suggestion for RNA-probe stains where both are actually available.
  const stainsWithSpotDetections = getStainsWithSpotDetectionAndCellSegmentation(orchestrationMetadata);
  const suggestedSpotDetectionActionsByStain = getSuggestedSpotDetectionActionsByStain(
    stainsWithSpotDetections,
    postProcessingActionsById,
    postProcessingActions,
    featureActions,
    stainTypeIdToDisplayName
  );

  const getExpectedSpotDetectionBinnedColumnIds = (stain: string) =>
    map(
      getOptions(
        InputSource.CELL_BINS,
        [stain],
        0,
        postProcessingActions,
        ActionInput.FEATURE_FAMILY,
        postProcessingActionsById
      ),
      'id'
    );

  const getExpectedSpotDetectionThresholdsByInstance = (stain: string): Record<string, number> => {
    const thresholdsByInstance: Record<string, number> = {};
    forEach(
      getInstanceCountThresholdOptions(
        getOptions(
          InputSource.INSTANCE_COUNT_THRESHOLDS,
          [stain],
          0,
          postProcessingActions,
          ActionInput.CELL,
          postProcessingActionsById
        )
      ),
      (option) => {
        thresholdsByInstance[option.instanceName] = option.defaultThreshold;
      }
    );
    return thresholdsByInstance;
  };

  // Existing Instance H-Score/Positivity actions whose configured columns/thresholds no longer match
  // the current H-score bins (e.g. a probe or dilation was added, or bins were edited).
  const outOfSyncSpotDetectionActionsByStain = getOutOfSyncSpotDetectionActions(
    stainsWithSpotDetections,
    postProcessingActions,
    featureActions,
    getExpectedSpotDetectionBinnedColumnIds,
    getExpectedSpotDetectionThresholdsByInstance,
    stainTypeIdToDisplayName
  );

  // Adds the recommended actions the user explicitly approved from the suggestion banner; never runs
  // automatically, since silently mutating the action list is not acceptable for this QMS-governed form.
  // Instance H-Score is a feature (featureActions), not a post-processing action, so each suggestion is
  // routed to the list it actually belongs to (see SpotDetectionActionSuggestion.isFeature).
  const addSuggestedSpotDetectionActions = (stain: string, suggestions: SpotDetectionActionSuggestion[]) => {
    const buildAction = ({ actionId }: SpotDetectionActionSuggestion) => ({
      ...createNewAction(actionId, postProcessingActionsById),
      stain,
    });
    const newPostProcessingActions = map(
      filter(suggestions, (suggestion) => !suggestion.isFeature),
      buildAction
    );
    const newFeatureActions = map(
      filter(suggestions, (suggestion) => suggestion.isFeature),
      buildAction
    );

    if (!isEmpty(newPostProcessingActions)) {
      setPostProcessingActions((prevActions) => {
        const nextActions = [...prevActions, ...newPostProcessingActions];
        updateClassConfigFromSelectedPostProcessingActions(nextActions);
        return nextActions;
      });
    }
    if (!isEmpty(newFeatureActions)) {
      setFeatureActions((prevActions) => [...prevActions, ...newFeatureActions]);
    }
  };

  // Overrides an existing Instance H-Score/Positivity action's bins-derived fields to match the
  // current H-score bins, discarding whatever was previously configured there. Only runs when the
  // user explicitly clicks "Update" for that stain from the suggestion banner.
  const updateOutOfSyncSpotDetectionActions = (stain: string, updates: SpotDetectionActionUpdate[]) => {
    const hScoreUpdate = find(updates, { actionName: INSTANCE_H_SCORE_ACTION_NAME });
    if (hScoreUpdate) {
      const expectedBinnedColumnIds = getExpectedSpotDetectionBinnedColumnIds(stain);
      setFeatureActions((prevActions) =>
        map(prevActions, (action) =>
          action.id === hScoreUpdate.actionId
            ? set(cloneDeep(action), INSTANCE_H_SCORE_BINNED_COLUMNS_INPUT_KEY, expectedBinnedColumnIds)
            : action
        )
      );
    }

    const positivityUpdate = find(updates, { actionName: INSTANCE_COUNT_POSITIVITY_ACTION_NAME });
    if (positivityUpdate) {
      const expectedThresholdsByInstance = getExpectedSpotDetectionThresholdsByInstance(stain);
      setPostProcessingActions((prevActions) =>
        map(prevActions, (action) =>
          action.id === positivityUpdate.actionId
            ? set(cloneDeep(action), INSTANCE_COUNT_THRESHOLDS_INPUT_KEY, expectedThresholdsByInstance)
            : action
        )
      );
    }
  };

  // combine selectedAssignmentDetails with the new selectedAssignmentDetails from result
  // to avoid duplicates, we use uniqWith and compare the properties of SelectedAssignmentDetails except for id (which is unique)
  const getNewSelectedAssignmentDetails = (
    selectedAssignmentDetails: AssignmentByStain,
    stainingType: string,
    result: Input
  ): SelectedAssignmentDetails[] => {
    return uniqWith(
      concat(
        get(selectedAssignmentDetails, stainingType, []),
        getSelectedAssignmentDetails(result, stainingType, annotationAssignmentsByStain)
      ),
      (a, b) => {
        return (
          a.modelType === b.modelType &&
          a.useClassesFromIgnore === b.useClassesFromIgnore &&
          isEqual(a.assignment, b.assignment) &&
          isEqual(a.classesToIgnore, b.classesToIgnore) &&
          isEqual(a.classesToUse, b.classesToUse)
        );
      }
    );
  };

  const setModelResults = (params: { inputs?: Inputs }, allInferenceResults: ModelInference[]) => {
    const restoredSelectedResultsSnapshot = buildPreviousJobSelectedResultsSnapshotFromInputs({
      params,
      slidesById: keyBy(slides, 'slideId'),
      allInferenceResults,
      postprocessedArtifacts,
      pipsResultsArtifacts,
      pipsAreaInstancesArtifacts,
      cellQuantificationArtifacts,
      areaQuantificationArtifacts,
      slideSegmentationsTableArtifacts,
      slideDetectionsTableArtifacts,
      classificationModelOptions,
      getNewSelectedAssignmentDetails,
    });

    setModelPreferencesState(restoredSelectedResultsSnapshot.modelPreferencesByModelId);
    setBinaryClassifierSelectionModeState(
      hasBinaryClassifierMultipleSelections(restoredSelectedResultsSnapshot.selectedModelOrchestrations)
        ? 'multiple'
        : 'single'
    );
    setSelectedOrchestrations(restoredSelectedResultsSnapshot.selectedModelOrchestrations);
    setSelectedPostprocessedOrchestrations(restoredSelectedResultsSnapshot.selectedPostprocessedOrchestrations);
    setSelectedPipsOrchestrations(restoredSelectedResultsSnapshot.selectedPipsOrchestrations);
    setSelectedPipsAreaInstancesOrchestrations(restoredSelectedResultsSnapshot.selectedPipsAreaInstancesOrchestrations);
    setSelectedCellQuantificationOrchestrations(
      restoredSelectedResultsSnapshot.selectedCellQuantificationOrchestrations
    );
    setSelectedAreaQuantificationOrchestrations(
      restoredSelectedResultsSnapshot.selectedAreaQuantificationOrchestrations
    );
    setSelectedSlideSegmentationsTableOrchestrations(
      restoredSelectedResultsSnapshot.selectedSlideSegmentationsTableOrchestrations
    );
    setSelectedSlideDetectionsTableOrchestrations(
      restoredSelectedResultsSnapshot.selectedSlideDetectionsTableOrchestrations
    );
    setSelectedAssignments(restoredSelectedResultsSnapshot.selectedAssignments);
  };

  // Keep class config in sync with current selections (important for rebuild, where selection state updates are async).
  useEffect(() => {
    setClassConfigFromSelectedResults();
  }, [
    selectedOrchestrations,
    selectedNonModelOrchestrationsForRun,
    selectedAssignments,
    modelPreferencesByModelId,
    postProcessingActions,
    cellRules,
    kMeansClusteringResults,
  ]);

  const getAdditionalInputErrorMessage = (
    inputValue: any,
    input: PostProcessingInput,
    action: PostProcessingActionCreated
  ): string => {
    const registrationsWithStains = getSlidesRegistrationWithStains(slides, selectedSlideRegistrations);
    return getAdditionalInputError(inputValue, input, action, slides, registrationsWithStains);
  };

  const isMultiplex = includes(slideStainTypes, MULTIPLEX_STAIN_ID);

  const {
    inferenceArtifacts,
    allNonModelArtifacts,
    postprocessedArtifacts,
    pipsResultsArtifacts,
    pipsAreaInstancesArtifacts,
    cellQuantificationArtifacts,
    areaQuantificationArtifacts,
    slideSegmentationsTableArtifacts,
    slideDetectionsTableArtifacts,
    isInferenceArtifactsLoading,
    isInferenceArtifactsFetched,
    isInferenceArtifactsFetching,
    isInferenceArtifactsError,
    isPostprocessedArtifactsLoading,
    isPostprocessedArtifactsFetched,
    isPostprocessedArtifactsFetching,
    isPostprocessedArtifactsError,
    isPipsResultsArtifactsLoading,
    isPipsResultsArtifactsFetched,
    isPipsResultsArtifactsFetching,
    isPipsAreaInstancesArtifactsLoading,
    isPipsAreaInstancesArtifactsFetched,
    isPipsAreaInstancesArtifactsFetching,
    isCellQuantificationArtifactsLoading,
    isCellQuantificationArtifactsFetched,
    isCellQuantificationArtifactsFetching,
    isAreaQuantificationArtifactsLoading,
    isAreaQuantificationArtifactsFetched,
    isAreaQuantificationArtifactsFetching,
    isSlideSegmentationsTableArtifactsLoading,
    isSlideSegmentationsTableArtifactsFetched,
    isSlideSegmentationsTableArtifactsFetching,
    isSlideDetectionsTableArtifactsLoading,
    isSlideDetectionsTableArtifactsFetched,
    isSlideDetectionsTableArtifactsFetching,
  } = useArtifactQueries({ casesParams, isMultiplex });

  const [currentPreset, setCurrentPreset] = useState<JobPreset | null>(null);
  const [persistedConfigurationSource, setPersistedConfigurationSource] = useQueryParam(
    'pipsConfigurationSource',
    StringParam
  );
  const [configurationSource, setConfigurationSource] = useState<ConfigurationSource>(() =>
    getInitialConfigurationSource({ jobId, persistedConfigurationSource })
  );

  useEffect(() => {
    if (!jobId && !isConfigurationSource(persistedConfigurationSource)) {
      setPersistedConfigurationSource('blank', 'replaceIn');
    }
  }, [jobId, persistedConfigurationSource, setPersistedConfigurationSource]);

  const { annotationAssignmentsByStain, finishLoadingAnnotationAssignments } = useAnnotationAssignmentByStain({
    casesParams,
    stainTypes: slideStainTypes,
  });

  const canRestorePreviousJobInferenceResults =
    isInferenceArtifactsFetched &&
    !isInferenceArtifactsFetching &&
    isPostprocessedArtifactsFetched &&
    !isPostprocessedArtifactsFetching &&
    isPipsResultsArtifactsFetched &&
    !isPipsResultsArtifactsFetching &&
    isPipsAreaInstancesArtifactsFetched &&
    !isPipsAreaInstancesArtifactsFetching &&
    isCellQuantificationArtifactsFetched &&
    !isCellQuantificationArtifactsFetching &&
    isAreaQuantificationArtifactsFetched &&
    !isAreaQuantificationArtifactsFetching &&
    isSlideSegmentationsTableArtifactsFetched &&
    !isSlideSegmentationsTableArtifactsFetching &&
    isSlideDetectionsTableArtifactsFetched &&
    !isSlideDetectionsTableArtifactsFetching &&
    finishLoadingAnnotationAssignments &&
    isSlidesFetched;

  const {
    selectedJobId,
    handleSelectedJob,
    isLoadingSelectedPreviousJob,
    setIsLoadingSelectedPreviousJob,
    job,
    isJobFetched,
    isJobLoading,
    isJobError,
    jobError,
    isRebuildInferenceResultsSet,
    hasLoadedPreviousJobSelections,
  } = usePreviousJobRestore({
    jobId,
    canApplyJobParams: !isEmpty(classConfigWithSpecialClasses),
    canRestoreInferenceResults: canRestorePreviousJobInferenceResults,
    setCalculateFeatureParams,
    applyPipsStudyInputs,
    applySlideRegistrations,
    setModelResults,
    setJobSummaryData,
    inferenceArtifacts,
    onSelectedJobReset: handlePreviousJobReset,
  });

  const [pipRunNameState, setPipRunName] = useState<string | undefined>(undefined);
  const pipsNameFromJob = job?.params?.name;
  const pipRunName = pipRunNameState || pipsNameFromJob || additionalParams?.pipRunName || defaultPipsRunName;

  useEffect(() => {
    setHidePostProcessingInputs(shouldHidePostProcessingInputsByDefault(pipRunName));
  }, [pipRunName]);

  const isGenericRun =
    pipRunName === PipRunNameEnum.GenericPipsRun ||
    pipsNameFromJob === PipRunNameEnum.GenericPipsRun ||
    // for legacy jobs rebuilds
    (Boolean(job) && !pipsNameFromJob);

  const previousJobSelectionStatus = usePreviousJobSelectionStatus({
    hasLoadedPreviousJobSelections,
    jobParams: job?.params as CalculateFeaturesParams | undefined,
    slides,
    inferenceArtifacts,
    postprocessedArtifacts,
    pipsResultsArtifacts,
    pipsAreaInstancesArtifacts,
    cellQuantificationArtifacts,
    areaQuantificationArtifacts,
    slideSegmentationsTableArtifacts,
    slideDetectionsTableArtifacts,
    classificationModelOptions,
    getNewSelectedAssignmentDetails,
    selectedModelOrchestrations: selectedOrchestrations,
    selectedPostprocessedOrchestrations,
    selectedPipsOrchestrations,
    selectedPipsAreaInstancesOrchestrations,
    selectedCellQuantificationOrchestrations,
    selectedAreaQuantificationOrchestrations,
    selectedSlideSegmentationsTableOrchestrations,
    selectedSlideDetectionsTableOrchestrations,
    selectedAssignments,
    modelPreferencesByModelId,
    selectedSlideRegistrations,
    perMarkerThresholds: thresholds,
    perMarkerThresholdsOrchestrationId,
    cellRules,
    knnArtifactUrl,
    restoredNeighborhoodClustersJobOrchestrationId,
  });

  const {
    postProcessingStatus: previousJobPostProcessingStatus,
    featureStatus: previousJobFeatureStatus,
    visualizationStatus: previousJobVisualizationStatus,
  } = usePreviousJobConfigurationStatus({
    hasLoadedPreviousJobSelections,
    jobParams: job?.params as CalculateFeaturesParams | undefined,
    postProcessingActionsById,
    postProcessingActions,
    featureActions,
    newCellsCreated,
    visualizations,
  });

  const casesManifestFromSelections = useMemo(
    () =>
      buildCasesManifest({
        slides,
        selectedOrchestrations,
        allSelectedNonModelOrchestrations: selectedNonModelOrchestrationsForRun,
        selectedAssignments,
      }),
    [slides, selectedOrchestrations, selectedNonModelOrchestrationsForRun, selectedAssignments]
  );

  const inputsPerCasePerSlideFromSelections = useMemo(
    () =>
      buildInputsPerCasePerSlide({
        slides,
        selectedOrchestrations,
        allSelectedNonModelOrchestrations: selectedNonModelOrchestrationsForRun,
        selectedAssignments,
        modelPreferencesByModelId,
        modelTypesByType,
        classificationModelOptions,
        casesManifest: casesManifestFromSelections,
        orchestrationMetadata,
      }),
    [
      slides,
      selectedOrchestrations,
      selectedNonModelOrchestrationsForRun,
      selectedAssignments,
      modelPreferencesByModelId,
      modelTypesByType,
      classificationModelOptions,
      casesManifestFromSelections,
      orchestrationMetadata,
    ]
  );

  const artifactCategoryByUrl = useMemo(
    () =>
      buildArtifactCategoryByUrl({
        postprocessedArtifacts,
        pipsResultsArtifacts,
        pipsAreaInstancesArtifacts,
        cellQuantificationArtifacts,
        areaQuantificationArtifacts,
        slideSegmentationsTableArtifacts,
        slideDetectionsTableArtifacts,
      }),
    [
      postprocessedArtifacts,
      pipsResultsArtifacts,
      pipsAreaInstancesArtifacts,
      cellQuantificationArtifacts,
      areaQuantificationArtifacts,
      slideSegmentationsTableArtifacts,
      slideDetectionsTableArtifacts,
    ]
  );

  const missingInputsBySlideId = useMemo(() => {
    if (isEmpty(slides)) {
      // Don't calculate missing inputs until slides are loaded, to avoid showing missing inputs snackbar on initial load
      return;
    }

    if (!isEmpty(job?.params?.inputs) && !isRebuildInferenceResultsSet) {
      // Wait for the job params to be set from the job before calculating missing inputs, to avoid showing missing inputs snackbar on initial load of a job with existing params
      return;
    }

    return getMissingInputsBySlideId({
      selectedSlideIds: map(slides, 'slideId'),
      inputs: inputsPerCasePerSlideFromSelections,
      artifactCategoryByUrl,
    });
  }, [
    slides,
    job?.params?.inputs,
    isRebuildInferenceResultsSet,
    inputsPerCasePerSlideFromSelections,
    artifactCategoryByUrl,
  ]);

  const runCalculateFeaturesMutation = useRunCalculateFeaturesMutation({
    onError: (error: unknown) => {
      const message =
        error instanceof Error && error.message
          ? `Calculate Features failed: ${error.message}`
          : 'Error occurred, Calculate Features failed';

      enqueueSnackbar(message, {
        variant: 'error',
      });
      console.error('Calculate Features error:', error);
      setActiveStep(0);
    },
    onSuccess: () => {
      enqueueSnackbar('Calculate Features Started', { variant: 'success' });
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_CALCULATE_FEATURES);
    },
  });

  const slidesById = keyBy(slides, 'slideId');

  const onSubmit = () => {
    if (thresholdJobSelectionMode === 'multiple' && thresholdMergeState.hasBlockingIssues) {
      enqueueSnackbar(
        !isEmpty(thresholdMergeState.invalidJobs)
          ? 'Selected threshold jobs must include per-marker thresholds before submitting.'
          : 'Resolve threshold job marker conflicts before submitting.',
        { variant: 'error' }
      );
      return;
    }

    const filteredThresholds = pickBy(thresholds, isNumber);
    const filteredCellRules = pickBy(cellRules, (cellRule) => Boolean(cellRule));
    const filteredKMeansClusteringResults = compact(kMeansClusteringResults);

    const params: {
      jobName: string;
      jobDescription: string;
      configParams: CalculateFeaturesParams;
      presetVersionId?: string;
      presetActionOverrides?: {
        postprocessing: Record<string, unknown>[];
        features: Record<string, unknown>[];
      };
    } = {
      jobName: jobSummaryData.name,
      jobDescription: jobSummaryData.description,
      configParams: {
        name: pipRunName,
        manifest: casesManifestFromSelections,
        inputs: inputsPerCasePerSlideFromSelections,
        registrations: flatMap(values(selectedSlideRegistrations)),
        postprocessing: convertPostProcessingActionsToBackend(postProcessingActionsById, postProcessingActions),
        features: convertFeatureActionsToBackend(postProcessingActionsById, featureActions, {
          ...newCellsCreated,
          ...spotDetectionCountBinCells,
        }),
        gridBasedCalculationParams,
        visualizations,
        studyId: casesParams?.studyId,
        pipsStudyInputs:
          !isEmpty(thresholds) ||
          !isEmpty(cellRules) ||
          !isEmpty(knnArtifactUrl) ||
          !isEmpty(knnFileType) ||
          !isEmpty(filteredKMeansClusteringResults)
            ? {
                ...(!isEmpty(filteredThresholds)
                  ? {
                      perMarkerThresholds: filteredThresholds,
                      ...(perMarkerThresholdsOrchestrationId ? { perMarkerThresholdsOrchestrationId } : {}),
                    }
                  : {}),
                ...(!isEmpty(filteredCellRules) ? { cellRules: filteredCellRules } : {}),
                ...(knnArtifactUrl || knnFileType
                  ? { knnClassifier: { artifactUrl: knnArtifactUrl, fileExtension: knnFileType } }
                  : {}),
                ...(restoredNeighborhoodClustersJobOrchestrationId
                  ? { neighborhoodClustersOrchestrationId: restoredNeighborhoodClustersJobOrchestrationId }
                  : {}),
                kMeansClusteringResults: filteredKMeansClusteringResults,
              }
            : undefined,
      },
    };
    if (currentPreset?.presetJson?.encapsulation) {
      params.presetVersionId = currentPreset.id;
      params.presetActionOverrides = {
        postprocessing: params.configParams.postprocessing || [],
        features: params.configParams.features || [],
      };
    }

    runCalculateFeaturesMutation.mutate(params);

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for Calculate Features to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_CALCULATE_FEATURES,
      autoHideDuration: null,
    });
  };

  const relevantModelTypes = uniq([
    ...getModelsTypeByModelInferences(inferenceArtifacts), // all the models (area, cell, nucleai, etc.)
    ...getResultNameTypesByOrchestrations(allNonModelArtifacts), // all the result names
    ...map(values(modelPreferencesByModelId), 'readAs'), // all the classification models (from the classification_models table- intensity, etc.)
    ...map(
      filter(modelTypes, (modelType) => modelType.trainingType === 'segmentation'),
      'type'
    ), // all the segmentation model types (for the relevant models for annotation assignments)
  ]);

  const {
    data: pipsNameOptions,
    isLoading: isLoadingPipsNameOptions,
    isError: isErrorPipsNameOptions,
  } = usePipsRunNames({ enabled: isGenericRun });

  const steps: PostProcessingAndCalculateFeaturesSteps[] = useMemo(
    () => [
      PostProcessingAndCalculateFeaturesSteps.StartFrom,
      ...(!isGenericRun ? [] : [PostProcessingAndCalculateFeaturesSteps.SelectPipsName]),
      PostProcessingAndCalculateFeaturesSteps.SelectModels,
      PostProcessingAndCalculateFeaturesSteps.Postprocessing,
      PostProcessingAndCalculateFeaturesSteps.Features,
      PostProcessingAndCalculateFeaturesSteps.GridBasedCalculation,
      PostProcessingAndCalculateFeaturesSteps.Visualizations,
      PostProcessingAndCalculateFeaturesSteps.SummaryAndSave,
    ],
    [isGenericRun]
  );

  const { hasPreviousJobs, isCheckingPreviousJobs } = usePreviousJobsAvailability(
    JobType.PostProcessingAndCalculateFeatures
  );

  const selectModelsStepIndex = indexOf(steps, PostProcessingAndCalculateFeaturesSteps.SelectModels);
  const startFromStepIndex = indexOf(steps, PostProcessingAndCalculateFeaturesSteps.StartFrom);
  const postProcessingStepIndex = indexOf(steps, PostProcessingAndCalculateFeaturesSteps.Postprocessing);
  const featuresStepIndex = indexOf(steps, PostProcessingAndCalculateFeaturesSteps.Features);
  const gridBasedCalculationStepIndex = indexOf(steps, PostProcessingAndCalculateFeaturesSteps.GridBasedCalculation);
  const visualizationsStepIndex = indexOf(steps, PostProcessingAndCalculateFeaturesSteps.Visualizations);
  const shouldShowSlideInputValidations = Boolean(selectedJobId) || maxStepReached > selectModelsStepIndex;
  const jobErrorMessage = useMemo(() => {
    if (!jobError) {
      return null;
    }

    return (
      get(jobError, 'response.body.message') ||
      get(jobError, 'response.body.error') ||
      get(jobError, 'response.text') ||
      get(jobError, 'message') ||
      "Error occurred, Can't get job data."
    );
  }, [jobError]);

  useEffect(() => {
    const sourceIsReady =
      configurationSource === 'blank' ||
      (configurationSource === 'preset' && Boolean(currentPreset)) ||
      (configurationSource === 'previousJob' && Boolean(selectedJobId) && !isJobError);
    setIsStepFailed((previous) => ({ ...previous, [startFromStepIndex]: !sourceIsReady }));
  }, [configurationSource, currentPreset, isJobError, selectedJobId, startFromStepIndex]);

  const selectConfigurationSource = async (nextSource: ConfigurationSource) => {
    if (configurationSource === nextSource) {
      return;
    }
    if (
      configurationSource &&
      maxStepReached > startFromStepIndex &&
      !(await confirmWithModal({
        title: 'Change configuration source?',
        text: 'Settings selected in later steps may be replaced by the new source.',
      }))
    ) {
      return;
    }

    if (nextSource !== 'previousJob' && selectedJobId) {
      handleSelectedJob(undefined);
    }
    if (nextSource !== 'preset') {
      setCurrentPreset(null);
    }
    setConfigurationSource(nextSource);
    setPersistedConfigurationSource(nextSource);
  };

  useEffect(() => {
    if (activeStep > selectModelsStepIndex && !isEmpty(missingInputsBySlideId) && !hasShownMissingInputsSnackbar) {
      // only show the snackbar once per missing inputs, to avoid spamming the user with snackbars
      enqueueSnackbar('Some selected slides are missing inputs. Review warnings in the slide summary.', {
        variant: 'warning',
      });
      setHasShownMissingInputsSnackbar(true);
    } else if (isEmpty(missingInputsBySlideId)) {
      // reset the snackbar shown state when there are no missing inputs, so that if user goes back and selects a job with missing inputs, the snackbar will show again
      setHasShownMissingInputsSnackbar(false);
    }
  }, [activeStep, missingInputsBySlideId, enqueueSnackbar, hasShownMissingInputsSnackbar]);

  const stepsMap = {
    [PostProcessingAndCalculateFeaturesSteps.StartFrom]: {
      label: PostProcessingAndCalculateFeaturesSteps.StartFrom,
      isLoading:
        configurationSource === 'previousJob' &&
        (isLoadingSelectedPreviousJob || (Boolean(selectedJobId) && isJobLoading)),
      content: (
        <StartFromStep
          value={configurationSource}
          onChange={selectConfigurationSource}
          previousJobsAvailable={hasPreviousJobs}
          checkingPreviousJobs={isCheckingPreviousJobs}
          presetContent={
            <PresetsStep
              currentPreset={currentPreset}
              setCurrentPreset={setCurrentPreset}
              currentStains={currentStains}
              setPipRunName={setPipRunName}
              defaultPresetFromPipRunName={null}
              setCalculateFeatureParams={setCalculateFeatureParams}
            />
          }
          previousJobContent={
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <LoadFromPreviousJobStep
                  jobType={JobType.PostProcessingAndCalculateFeatures}
                  onSelectedJob={handleSelectedJob}
                  onSelectedJobLoadingChange={setIsLoadingSelectedPreviousJob}
                  selectedJobId={selectedJobId}
                />
              </Grid>
              {isJobError && jobErrorMessage && (
                <Grid item>
                  <Alert severity="error">{jobErrorMessage}</Alert>
                </Grid>
              )}
              {shouldShowSlideInputValidations && !isEmpty(selectedJobId) && !isEmpty(missingInputsBySlideId) && (
                <Grid item>
                  <Alert severity="warning">
                    There are missing inputs for {size(missingInputsBySlideId)}{' '}
                    {pluralize('slide', size(missingInputsBySlideId))}. Review warnings in the slide summary to resolve
                    or remove them.
                  </Alert>
                </Grid>
              )}
            </Grid>
          }
        />
      ),
      onNextOrBackClick: () => {
        setIsStepFailed((prev) => ({
          ...prev,
          [postProcessingStepIndex]: !arePostProcessingActionsValid({
            actions: postProcessingActions,
            postProcessingActionsById,
            getOptions,
            getMappingFiltersMetadataForLogicalQuery,
          }),
          [featuresStepIndex]: !arePostProcessingActionsValid({
            actions: featureActions,
            indexForOptions: size(postProcessingActions),
            actionsForOptions: postProcessingActions,
            postProcessingActionsById,
            getOptions,
            getMappingFiltersMetadataForLogicalQuery,
          }),
          [visualizationsStepIndex]: !isVisualizationStepValid(
            visualizations,
            classConfigWithSpecialClasses,
            postProcessingClasses
          ),
        }));
      },
    },
    [PostProcessingAndCalculateFeaturesSteps.SelectPipsName]: {
      label: PostProcessingAndCalculateFeaturesSteps.SelectPipsName,
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            {isLoadingPipsNameOptions ? (
              <Skeleton variant="text" height={40} width={200} />
            ) : isErrorPipsNameOptions ? (
              <Typography variant="body1" color="error">
                Error loading pipeline stage names
              </Typography>
            ) : (
              <Select
                value={pipRunName}
                onChange={(event) => setPipRunName(event.target.value as string)}
                variant="outlined"
                fullWidth
                disabled={isLoadingPipsNameOptions}
              >
                {map(pipsNameOptions, (pipNameOption: PipRunName) => (
                  <MenuItem key={pipNameOption.id} value={pipNameOption.name}>
                    {pipNameOption.name === defaultPipsRunName ? 'Post Processing' : humanize(pipNameOption.name)}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Grid>
        </Grid>
      ),
    },
    [PostProcessingAndCalculateFeaturesSteps.SelectModels]: {
      label: PostProcessingAndCalculateFeaturesSteps.SelectModels,
      subLabel: selectedJobId
        ? 'Rebuilt from previous job. Please wait for the data to load, before proceeding.'
        : undefined,
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <InferenceModelsForSlides
              studyId={casesParams?.studyId}
              slides={slides}
              inferenceModels={inferenceArtifacts}
              postprocessedArtifacts={postprocessedArtifacts}
              pipsResultsArtifacts={pipsResultsArtifacts}
              cellQuantificationArtifacts={cellQuantificationArtifacts}
              areaQuantificationArtifacts={areaQuantificationArtifacts}
              slideSegmentationsTableArtifacts={slideSegmentationsTableArtifacts}
              pipsAreaInstancesArtifacts={pipsAreaInstancesArtifacts}
              slideDetectionsTableArtifacts={slideDetectionsTableArtifacts}
              modelsType={relevantModelTypes}
              missingInputsBySlideId={missingInputsBySlideId}
              showValidationMessages={shouldShowSlideInputValidations}
              isLoading={!isSlidesFetched}
              isLoadingInferenceResults={isInferenceArtifactsLoading || isInferenceArtifactsFetching}
              isLoadingAssignmentAnnotations={!finishLoadingAnnotationAssignments}
              isLoadingPostProcessing={isPostprocessedArtifactsLoading || isPostprocessedArtifactsFetching}
              isLoadingPipsResults={isPipsResultsArtifactsLoading || isPipsResultsArtifactsFetching}
              isLoadingPipsAreaInstances={isPipsAreaInstancesArtifactsLoading || isPipsAreaInstancesArtifactsFetching}
              isLoadingQuantification={
                isCellQuantificationArtifactsLoading ||
                isCellQuantificationArtifactsFetching ||
                isAreaQuantificationArtifactsLoading ||
                isAreaQuantificationArtifactsFetching
              }
              isLoadingSlideSegmentationsTable={
                isSlideSegmentationsTableArtifactsLoading || isSlideSegmentationsTableArtifactsFetching
              }
              isLoadingSlideDetectionsTable={
                isSlideDetectionsTableArtifactsLoading || isSlideDetectionsTableArtifactsFetching
              }
              {...orchestrationSelectionsState}
              displayAssignmentAnnotations
              displayPostProcessing={!hidePostProcessingInputs}
              displayPipsResults={!hidePostProcessingInputs}
              displayPipsAreaInstancesResults={!hidePostProcessingInputs}
              hidePostProcessingInputs={hidePostProcessingInputs}
              setHidePostProcessingInputs={setHidePostProcessingInputs}
              displayQuantification
              displaySlideSegmentationsTable
              displaySlideDetectionsTable
              autoSelectLatestFallbackInputs={isEmpty(selectedJobId)}
              assignmentAnnotationsByStainType={annotationAssignmentsByStain}
              {...assignmentManagementState}
              modelPreferencesByModelId={modelPreferencesByModelId}
              setModelPreferencesByModelId={setModelPreferencesByModelId}
              setIntensityClassificationBinning={setIntensityClassificationBinning}
              previousJobComparisonStatus={previousJobSelectionStatus}
              {...slideRegistrationsContext}
              {...(includes(slideStainTypes, MULTIPLEX_STAIN_ID)
                ? {
                    multiplexGeneralInputsProps: {
                      thresholdJobSelectionMode,
                      setThresholdJobSelectionMode,
                      selectedThresholdJobId,
                      setSelectedThresholdJobId,
                      selectedThresholdJobIds,
                      setSelectedThresholdJobIds,
                      thresholdMergeState,
                      setThresholdConflictResolutions,
                      restoredThresholdJobOrchestrationId,
                      onThresholdJobSelect,
                      onThresholdJobsSelect,
                      onCellRulesChange,
                      restoredCellRules,
                      selectedCellRulesPanelId,
                      setSelectedCellRulesPanelId,
                      selectedCellTypingJobId,
                      setSelectedCellTypingJobId,
                      restoredCellTypingJobOrchestrationId,
                      onCellTypingJobSelect,
                      selectedNeighborhoodClustersJobId,
                      setSelectedNeighborhoodClustersJobId,
                      restoredNeighborhoodClustersJobOrchestrationId,
                      onNeighborhoodClustersJobSelect,
                      binaryClassifierSelectionMode,
                      setBinaryClassifierSelectionMode,
                      cellTypingJobError,
                      pipRunName,
                      autoAssignLastJob: isEmpty(selectedJobId),
                      autoAssignLastPanel: isEmpty(selectedJobId),
                    },
                  }
                : {})}
            />
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => {
        setClassConfigFromSelectedResults();
        setIsStepFailed((prev) => ({
          ...prev,
          [selectModelsStepIndex]: !isInferenceResultsStepValid(
            slides,
            selectedOrchestrations,
            selectedNonModelOrchestrationsForRun,
            selectedAssignments,
            relevantModelTypes,
            selectedSlideRegistrations
          ),
        }));
      },
    },
    [PostProcessingAndCalculateFeaturesSteps.Postprocessing]: {
      label: PostProcessingAndCalculateFeaturesSteps.Postprocessing,
      content: (
        <>
          <SpotDetectionActionSuggestionsBanner
            suggestionsByStain={suggestedSpotDetectionActionsByStain}
            onAddStainActions={addSuggestedSpotDetectionActions}
            updatesByStain={outOfSyncSpotDetectionActionsByStain}
            onUpdateStainActions={updateOutOfSyncSpotDetectionActions}
          />
          <PostProcessingActions
            actions={postProcessingActions}
            setActions={setPostProcessingActions}
            previousJobComparisonStatus={previousJobPostProcessingStatus}
            highlightedInputs={highlightedInputs}
            stainTypes={stainTypeOptions}
            taxonomies={taxonomies}
            qcMarkersSettings={qcMarkersSettings}
            updateClassConfigAccordingToActions={updateClassConfigFromSelectedPostProcessingActions}
            getOptions={(inputSource, selectedStains, index, actionInput, inputSourceDependentOn) =>
              getOptions(
                inputSource,
                selectedStains,
                index,
                postProcessingActions,
                actionInput,
                postProcessingActionsById,
                inputSourceDependentOn
              )
            }
            getOptionsByValue={(inputSource, selectedStains, index, actionInput, inputSourceDependentOn) =>
              getOptionsByValue(
                inputSource,
                selectedStains,
                index,
                postProcessingActions,
                actionInput,
                postProcessingActionsById,
                inputSourceDependentOn
              )
            }
            getMappingFiltersMetadataForLogicalQuery={(stain, actionInput, actionAttributesTable, index) =>
              getMappingFiltersMetadataForLogicalQuery(
                stain,
                index,
                postProcessingActions,
                actionInput,
                postProcessingActionsById,
                actionAttributesTable
              )
            }
          />
        </>
      ),
      onNextOrBackClick: () => {
        setPostProcessingClasses(getOutputNamesForAllStains(size(postProcessingActions), postProcessingActions));
        setIsStepFailed((prev) => ({
          ...prev,
          [postProcessingStepIndex]: !arePostProcessingActionsValid({
            actions: postProcessingActions,
            postProcessingActionsById,
            getOptions,
            getMappingFiltersMetadataForLogicalQuery,
          }),
          [visualizationsStepIndex]: !isVisualizationStepValid(
            visualizations,
            classConfigWithSpecialClasses,
            postProcessingClasses
          ),
        }));
      },
    },
    [PostProcessingAndCalculateFeaturesSteps.Features]: {
      label: PostProcessingAndCalculateFeaturesSteps.Features,
      content: (
        <FeatureActions
          stains={currentStains}
          actions={featureActions}
          setActions={setFeatureActions}
          previousJobComparisonStatus={previousJobFeatureStatus}
          highlightedInputs={highlightedInputs}
          stainTypes={stainTypeOptions}
          taxonomies={taxonomies}
          qcMarkersSettings={qcMarkersSettings}
          newCells={newCellsCreated}
          setNewCells={setNewCellsCreated}
          getOptions={(inputSource, selectedStains, index, actionInput, inputSourceDependentOn) =>
            getOptions(
              inputSource,
              selectedStains,
              size(postProcessingActions),
              postProcessingActions,
              actionInput,
              postProcessingActionsById,
              inputSourceDependentOn
            )
          }
          getOptionsByValue={(inputSource, selectedStains, index, actionInput, inputSourceDependentOn) =>
            getOptionsByValue(
              inputSource,
              selectedStains,
              size(postProcessingActions),
              postProcessingActions,
              actionInput,
              postProcessingActionsById,
              inputSourceDependentOn
            )
          }
          getMappingFiltersMetadataForLogicalQuery={(stain, actionInput, actionAttributesTable) =>
            getMappingFiltersMetadataForLogicalQuery(
              stain,
              size(postProcessingActions),
              postProcessingActions,
              actionInput,
              postProcessingActionsById,
              actionAttributesTable
            )
          }
          getAdditionalInputError={getAdditionalInputErrorMessage}
        />
      ),
      onNextOrBackClick: () => {
        setIsStepFailed((prev) => ({
          ...prev,
          [featuresStepIndex]: !arePostProcessingActionsValid({
            actions: featureActions,
            indexForOptions: size(postProcessingActions),
            actionsForOptions: postProcessingActions,
            postProcessingActionsById,
            getOptions,
            getMappingFiltersMetadataForLogicalQuery,
          }),
        }));
      },
    },
    [PostProcessingAndCalculateFeaturesSteps.GridBasedCalculation]: {
      label: PostProcessingAndCalculateFeaturesSteps.GridBasedCalculation,
      content: (
        <>
          {isEmpty(gridBasedFeaturesStains) ? (
            <Typography>No grid based features selected, you can skip this step</Typography>
          ) : (
            map(gridBasedFeaturesStains, (stain) => (
              <GridBasedCalculationParamsStep
                key={stain}
                stain={stain}
                gridBasedCalculationParams={
                  gridBasedCalculationParams[stain] || {
                    gridSizeUm: [],
                    minCellsThreshold: {},
                    minAreaFractionThreshold: {},
                  }
                }
                setGridBasedCalculationParams={(params: GridBasedCalculationParams) =>
                  setGridBasedCalculationParams((prev) => ({ ...prev, [stain]: params }))
                }
              />
            ))
          )}
        </>
      ),
      onNextOrBackClick: () => {
        setIsStepFailed((prev) => {
          return {
            ...prev,
            [gridBasedCalculationStepIndex]: !isGridParamsStepValid(
              gridBasedFeaturesStains,
              gridBasedCalculationParams
            ),
          };
        });
      },
    },
    [PostProcessingAndCalculateFeaturesSteps.Visualizations]: {
      label: 'Visualization',
      content: (
        <VisualizationStep
          classConfig={classConfigWithSpecialClasses}
          postProcessingClasses={postProcessingClasses}
          previousJobComparisonStatus={previousJobVisualizationStatus}
          visualizations={visualizations}
          setVisualizations={setVisualizations}
          stains={currentStains}
          classificationModelOptions={selectedClassificationModelsOptions}
        />
      ),
      onNextOrBackClick: () => {
        setIsStepFailed((prev) => ({
          ...prev,
          [visualizationsStepIndex]: !isVisualizationStepValid(
            visualizations,
            classConfigWithSpecialClasses,
            postProcessingClasses
          ),
        }));
      },
    },
    [PostProcessingAndCalculateFeaturesSteps.SummaryAndSave]: {
      label: PostProcessingAndCalculateFeaturesSteps.SummaryAndSave,
      content: (
        <Grid alignContent="flex-start">
          <SaveUpdatePresetButtons
            currentPreset={currentPreset}
            pipRunName={pipRunName}
            modelPreferencesByModelId={modelPreferencesByModelId}
            postProcessingActions={postProcessingActions}
            featureActions={featureActions}
            gridBasedCalculationParams={gridBasedCalculationParams}
            visualizations={visualizations}
            currentStains={currentStains}
            postProcessingActionsById={postProcessingActionsById}
            newCellsCreated={newCellsCreated}
            onPresetChange={setCurrentPreset}
            highlightedInputs={highlightedInputs}
          />
          <Grid container direction="column" spacing={2} mt={1}>
            <Grid item>
              <TextField
                label="Job Name"
                fullWidth
                value={jobSummaryData.name}
                onChange={(e) => setJobSummaryData((prev) => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item>
              <TextField
                label="Job Description"
                fullWidth
                value={jobSummaryData.description}
                onChange={(e) => setJobSummaryData((prev) => ({ ...prev, description: e.target.value }))}
                multiline
                minRows={4}
              />
            </Grid>
          </Grid>
        </Grid>
      ),
    },
  };

  const message = isEmpty(casesParams?.studyId)
    ? 'No study selected'
    : modelTypesError || isInferenceArtifactsError || isPostprocessedArtifactsError || isSlidesError
    ? "Error occurred, Can't get data from the server."
    : null;

  return (
    <DialogWithCloseButton header="Post Processing and Feature Calculation" onClose={onClose} fullScreen={true}>
      <Box
        sx={{
          // Allow flex/grid children to shrink so wide content (e.g. the dense
          // cell-rules DataGrid) scrolls inside its own container instead of
          // widening the whole dialog. Widths are left to MUI so column layouts
          // are preserved.
          '& .MuiGrid-root': { minWidth: 0 },
        }}
      >
        {platformVersionWarning}
        {!isEmpty(selectedJobId) &&
          isJobFetched &&
          isRebuildInferenceResultsSet &&
          !isEmpty(selectedPostprocessedOrchestrations) && (
            <Typography variant="h4" color="error" mb={2}>
              Please note: Some legacy post-processed orchestrations are selected, so errors may occur during the
              post-processing actions or features steps. If any issues arise, please contact Software Support.
            </Typography>
          )}
        {!isEmpty(message) ? (
          <Typography>{message}</Typography>
        ) : (
          <ActionStepper
            validateStep={async () => {
              return true;
            }}
            handleSubmit={() => {
              onSubmit();
            }}
            steps={map(steps, (step) => stepsMap[step])}
            activeStateHandlers={[activeStep, setActiveStep]}
            setActiveStepForValidation={setActiveStep}
            isStepFailed={isStepFailed}
          />
        )}
      </Box>
    </DialogWithCloseButton>
  );
};
